import './Participants.scss';

import { Refresh } from '@mui/icons-material';
import { Alert, Button, ThemeProvider } from '@mui/material';
import { EventStatusEnum } from 'domain/event/EventStatusEnum';
import {
  EventParticipant,
  EventParticipantFilters,
} from 'domain/event/participants/EventParticipant';
import { getIsRandomTeamState } from 'domain/event/UnpyEvent.func';
import ModalStartEvent from 'primary/classements/Ligue/Modal/StartEvent/ModalStartEvent';
import { PageLayout } from 'primary/Components/PageLayout/PageLayout';
import { Title } from 'primary/Components/Title/Title';
import { EVENT_CONTEXT } from 'primary/context/event/EventContext';
import { EventParticipantDatatable } from 'primary/events/datatable/EventParticipantDatatableParameters';
import { ActionModalAddParticipant } from 'primary/events/modals/ActionModalAddParticipant';
import { useBooleanToggle } from 'primary/hooks/useBooleanToggle';
import { useContextDependency } from 'primary/hooks/useContextDependency';
import { useFetchWithFilterPage } from 'primary/hooks/useFetchWithFilterPage';
import { useResetFiltersOnUnmount } from 'primary/hooks/useResetFiltersOnUnmount';
import { useRetrieveFromDomain } from 'primary/hooks/useRetrieveFromDomain';
import { useTranslate } from 'primary/hooks/useTranslate';
import ModalContainer from 'primary/ModalContainer/ModalContainer';
import React, { useContext } from 'react';

import { themeParameters } from '../parameters.helper';

export const Participants = () => {
  const { event } = useContext(EVENT_CONTEXT);
  const { eventParticipantRepository, authRepository } = useContextDependency();
  const t = useTranslate();
  const staticFilters = {
    idEvent: event?.id,
  };
  const useFetchReturn = useFetchWithFilterPage<
    EventParticipant,
    EventParticipantFilters
  >({
    keyStoreFilters: 'event-participants-param-list',
    staticFilters: staticFilters,
    filters: {},
    callApi: (filters, page) => eventParticipantRepository.search(filters, page, 10),
  });
  const [isOpenModalGenerate, , openModalGenerate, closeModalGenerate] =
    useBooleanToggle();
  const [participantsState, , , retry] = useRetrieveFromDomain(
    () => eventParticipantRepository.getEventParticipantsState(event?.id as number),
    undefined,
    !!event?.id,
  );

  if (!event) return null;

  useResetFiltersOnUnmount(useFetchReturn.reset);

  return (
    <div className={'eventParticipants'}>
      <PageLayout>
        <ThemeProvider theme={themeParameters}>
          <div className={'eventParticipants__header'}>
            <Title
              title={t('event.parameters.participants.title')}
              level={1}
              compensatePadding
              threeQuarter
              bordered
            />
            <div className={'-actions'}>
              <ActionModalAddParticipant
                event={event}
                disabled={event.status !== EventStatusEnum.PUBLISH}
              />
              {event.randomTeams && event.status === EventStatusEnum.ON_GOING && (
                <Button
                  variant={'contained'}
                  color={'primary'}
                  onClick={openModalGenerate}
                  className={'cancel'}
                >
                  <Refresh />
                  {t('classement.parameters.regenerateTeam')}
                </Button>
              )}
            </div>
            {event.status !== EventStatusEnum.PUBLISH &&
              event?.status === EventStatusEnum.DRAFT && (
                <span>
                  Votre événement doit être publié pour ajouter des participants
                </span>
              )}
            {event.status !== EventStatusEnum.PUBLISH &&
              event?.status === EventStatusEnum.ON_GOING && (
                <span>
                  Votre événement est démarré, vous ne pouvez ni ajouter ni supprimer des
                  participants
                </span>
              )}
          </div>
          {getIsRandomTeamState(event) && (
            <Alert severity={'info'}>
              <span>{t('event.parameters.participants.randomTeamNotice')}</span>
            </Alert>
          )}
          <EventParticipantDatatable
            participantsState={participantsState}
            useFetchReturn={useFetchReturn}
            reFetch={() => {
              retry();
              useFetchReturn.retry();
            }}
            event={event}
            readOnly={[EventStatusEnum.FINISH, EventStatusEnum.CANCELED].includes(
              event.status,
            )}
          />
          <ModalContainer
            handleClose={closeModalGenerate}
            isOpen={isOpenModalGenerate}
            content={
              <ModalStartEvent
                regenerate
                handleClose={closeModalGenerate}
                event={event}
                onStarted={useFetchReturn.retry}
              />
            }
          />
        </ThemeProvider>
      </PageLayout>
    </div>
  );
};
